import Papa from 'papaparse';

/**
 * 城市用水数据工具
 * 支持多指标CSV数据的解析、处理和导出
 */

const DATA_BASE_PATH = '/data/cityWater/';

export const loadCityIndicatorData = async (cityFileName) => {
  try {
    const response = await fetch(`${DATA_BASE_PATH}${cityFileName}`);
    if (!response.ok) throw new Error(`文件 ${cityFileName} 不存在`);
    const text = await response.text();
    return parseCityIndicatorCSV(text);
  } catch (e) {
    console.error('加载城市数据失败', e);
    throw e;
  }
};

export const parseCityIndicatorCSV = (csvText) => {
  const result = Papa.parse(csvText, {
    header: true,
    skipEmptyLines: true,
    transformHeader: h => h.trim(),
    transform: v => v === undefined ? v : v.trim()
  });

  if (result.errors && result.errors.length) {
    console.warn('CSV解析警告:', result.errors);
  }

  const rows = result.data;
  if (!rows || rows.length === 0) return { headers: [], data: [] };

  // headers
  const headers = Object.keys(rows[0]).map(h => h.trim());

  // normalize rows: convert numeric-like to Number, keep others as string
  const data = rows.map(r => {
    const obj = {};
    headers.forEach(h => {
      let v = r[h];
      if (v === undefined || v === null) {
        obj[h] = '';
      } else {
        const num = parseFloat(String(v).replace(/,/g, ''));
        obj[h] = isNaN(num) ? String(v) : num;
      }
    });
    return obj;
  }).filter(r => r[headers[0]] !== undefined && r[headers[0]] !== '');

  return { headers, data };
};

export const getAvailableIndicators = (parsed) => {
  if (!parsed || !parsed.headers) return [];
  // 默认第一个列是年份/时间
  return parsed.headers.slice(1);
};

export const getYearRange = (parsed) => {
  if (!parsed || !parsed.data || parsed.data.length === 0) return { min: null, max: null };
  const yearKey = parsed.headers[0];
  const years = parsed.data.map(r => {
    const v = r[yearKey];
    return typeof v === 'number' ? v : parseInt(String(v).slice(0, 4));
  }).filter(y => !isNaN(y));
  const min = Math.min(...years);
  const max = Math.max(...years);
  return { min, max };
};

export const filterByYearRange = (parsed, startYear, endYear) => {
  if (!parsed) return parsed;
  const yearKey = parsed.headers[0];
  const data = parsed.data.filter(r => {
    const v = r[yearKey];
    const y = typeof v === 'number' ? v : parseInt(String(v).slice(0, 4));
    return !isNaN(y) && y >= startYear && y <= endYear;
  });
  return { headers: parsed.headers, data };
};

export const formatForChart = (parsed, indicators) => {
  if (!parsed) return { x: [], series: [] };
  const yearKey = parsed.headers[0];
  const x = parsed.data.map(r => String(r[yearKey]));
  const series = indicators.map(ind => ({
    name: ind,
    data: parsed.data.map(r => {
      const v = r[ind];
      return (v === '' || v === null || v === undefined) ? null : Number(v);
    })
  }));
  return { x, series };
};

export const compareCitiesData = (parsedList, indicator) => {
  // parsedList: [{city, parsed}]
  const yearKey = parsedList[0]?.parsed?.headers?.[0];
  const years = parsedList[0]?.parsed?.data.map(r => String(r[yearKey])) || [];
  const series = parsedList.map(p => ({
    name: p.city,
    data: (p.parsed.data || []).map(r => {
      const v = r[indicator];
      return (v === '' || v === null || v === undefined) ? null : Number(v);
    })
  }));
  return { x: years, series };
};

export const exportParsedData = (parsed, fileName = 'city_data_export') => {
  if (!parsed || !parsed.headers) return;
  const csv = Papa.unparse([parsed.headers, ...parsed.data.map(r => parsed.headers.map(h => r[h]))]);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', `${fileName}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const cityWaterDataUtils = {
  loadCityIndicatorData,
  parseCityIndicatorCSV,
  getAvailableIndicators,
  getYearRange,
  filterByYearRange,
  formatForChart,
  compareCitiesData,
  exportParsedData
};

export default cityWaterDataUtils;